import type { HabitRowView, Schedule } from '@aj/core';
import {
  Box,
  CompletionToggle,
  DayLine,
  DayStrip,
  DayStripHeader,
  HStack,
  HabitCard,
  Icon,
  Pressable,
  Text,
  VStack,
} from '@aj/ui';
import type { DayCell, DayColumn, DayPoint } from '@aj/ui';
import { LEAD_WIDTH, TODAY_TRACK } from '@aj/ui';

import { useT } from '../../providers/I18nProvider.js';
import { useDragSort } from './useDragSort.js';
import type { MessageKey } from '@aj/i18n';

/** The desktop wall chart: a column per day, keyed to the same habits. */
export type HabitDays = {
  /** Oldest → today. The last column is today and is NOT drawn by the strip —
   *  the completion circle stands in it. */
  columns: readonly DayColumn[];
  /** Cells per habit id, same order as `columns`. */
  cells: Record<string, readonly DayCell[]>;
  onToggleDay: (habitId: string, columnIndex: number) => void;
};

export type HabitListProps = {
  habits: HabitRowView[];
  /** Schedules, keyed by habit id — only non-daily ones become subtext. */
  schedules: Record<string, Schedule>;
  /** Present on a desktop only. Absent, this is the phone list, unchanged. */
  days?: HabitDays;
  /**
   * Each habit's own kept-rate over the window on screen, keyed by id.
   *
   * The most actionable number in the product: an overall 74% says nothing,
   * but "Wake early — 40%" says what to change or drop. Desktop only, because
   * it belongs beside the history it summarises.
   */
  keptRatios?: Record<string, number | null>;
  /** The daily line above the chart. Absent leaves the chart as it was. */
  dayLine?: { points: readonly DayPoint[]; label: string };
  label: string;
  onToggle: (habitId: string) => void;
  /** Keep the promise at its floor. Absent on layouts that don't offer it. */
  onKeepFloor?: (habitId: string) => void;
  onReorder: (from: number, to: number) => void;
};

/**
 * The list. Everything above it is context; this is the product.
 *
 * ONE list, two widths. On a phone a row is a card with a completion circle;
 * on a desktop the same row grows a month of squares to its left of the
 * circle, which is the tracker students already screenshot. There is never a
 * second display of habits on screen — the grid IS the list.
 *
 * There is no reorder mode either: the grip is always there, and grabbing it
 * is the whole gesture. That works because the targets can't be confused — the
 * grip is on the left and only responds to a drag, the circle is on the right
 * and only responds to a tap.
 */
export function HabitList({
  habits,
  schedules,
  days,
  keptRatios,
  dayLine,
  label,
  onToggle,
  onKeepFloor,
  onReorder,
}: HabitListProps) {
  const t = useT();
  const drag = useDragSort(onReorder);

  // Today is the last column, and the completion circle sits in it.
  const pastColumns = days ? days.columns.slice(0, -1) : [];

  const list = (
    <VStack gap={days ? 0 : 2} as="ul" ref={drag.listRef} aria-label={label}>
      {habits.map((habit, index) => {
        const schedule = schedules[habit.id];
        // The plan outranks the schedule as subtext. "After Maghrib, at my
        // desk" is the cue that makes the habit happen; "3× a week" is
        // bookkeeping the student already knows. Rehearsing the if-then is
        // itself a moderator of the effect, so showing it is doing work.
        const subtext = planLabel(habit, t) ?? (schedule ? scheduleLabel(schedule, t) : undefined);
        const lifted = drag.dragging === index;
        const style = drag.rowStyle(index);
        const cells = days?.cells[habit.id];
        const kept = keptRatios?.[habit.id];

        return (
          <HabitCard
            key={habit.id}
            name={habit.name}
            done={habit.done}
            variant={days ? 'row' : 'card'}
            // Folding is what keeps a finished list short without ever
            // resorting it — the row stays exactly where the student put it.
            folded={habit.done && !lifted}
            className={lifted ? 'shadow-md' : ''}
            {...(style === undefined ? {} : { style })}
            {...(subtext === undefined ? {} : { subtext })}
            // The floor, offered only when there is one and the day is still
            // open. It disappears the moment anything is recorded, so the row
            // never shows two ways to do the same thing.
            {...(onKeepFloor && habit.floor && !habit.done
              ? {
                  below: (
                    <Pressable
                      onClick={() => onKeepFloor(habit.id)}
                      className="rounded-full border border-line px-2.5 py-1 text-left text-xs text-subtle hover:border-accent hover:text-accent"
                    >
                      {t('floor.action', { floor: habit.floor })}
                    </Pressable>
                  ),
                }
              : {})}
            leading={
              <Pressable
                aria-label={t('home.dragHandle', { name: habit.name })}
                // The browser must not claim this gesture for scrolling.
                className="-my-2 -ml-2 grid size-11 shrink-0 cursor-grab touch-none place-items-center rounded-full text-subtle active:scale-100 active:cursor-grabbing"
                {...drag.handleProps(index)}
              >
                <Icon name="grip" size={20} />
              </Pressable>
            }
            trailing={
              <Box className="flex shrink-0 items-center">
                {/* Left of the history it describes, so the eye reads the
                    summary and then the evidence for it. A bar AND the number:
                    the bar makes a column of habits comparable at a glance,
                    the number is the precision the bar gives up.

                    Hidden rather than zeroed for a habit too young to judge —
                    a bar at 0% on day two is a verdict on nobody. */}
                {days && (
                  <Box className="w-24 shrink-0 pr-3">
                    {kept != null && (
                      <HStack gap={2} align="center" justify="end">
                        <Box
                          data-coach="kept-rate"
                          className="h-1.5 w-11 shrink-0 overflow-hidden rounded-full bg-sunken"
                          title={t('home.percent', { value: Math.round(kept * 100) })}
                        >
                          <Box
                            className="h-full rounded-full bg-accent"
                            style={{ width: `${Math.max(2, Math.round(kept * 100))}%` }}
                          />
                        </Box>
                        <Text variant="caption" tone="muted" numeric className="w-8 text-right">
                          {t('home.percent', { value: Math.round(kept * 100) })}
                        </Text>
                      </HStack>
                    )}
                  </Box>
                )}
                {days && cells && (
                  <DayStrip
                    columns={pastColumns}
                    cells={cells}
                    onToggle={(columnIndex) => days.onToggleDay(habit.id, columnIndex)}
                  />
                )}
                <Box
                  className={
                    days
                      ? `${TODAY_TRACK} grid shrink-0 place-items-center self-stretch`
                      : 'shrink-0 pr-1'
                  }
                >
                  <CompletionToggle
                    data-coach="habit-row"
                    checked={habit.done}
                    partial={habit.level === 'floor'}
                    onChange={() => onToggle(habit.id)}
                    label={habit.name}
                  />
                </Box>
              </Box>
            }
          />
        );
      })}
    </VStack>
  );

  if (!days) return list;

  // One scroll container for header and rows together — that is what lets the
  // name column and the date header stay put while the months slide past.
  return (
    <Box className="overflow-x-auto">
      <Box className="relative w-max">
        {/* Today, as ONE band from the date down past the last row rather than
            a tint per cell. Painted over the top so the row hairlines don't
            cut it into segments, and inert so the circles underneath stay
            tappable. Today is always the last column, hence `right-0`. */}
        <Box
          aria-hidden
          className={`pointer-events-none absolute inset-y-0 right-0 ${TODAY_TRACK} rounded-md bg-accent-soft`}
        />
        {/* Drawn inside this container, so it scrolls with the days and lands
            over its own columns without a second copy of the geometry. The
            bar column is spaced for separately: the line describes the DAYS,
            and the bars describe the habits. */}
        {dayLine && (
          <Box className="flex items-end">
            <Box className={`${LEAD_WIDTH} sticky left-0 z-20 shrink-0 bg-surface`} />
            <Box className="w-24 shrink-0" />
            <DayLine points={dayLine.points} label={dayLine.label} />
          </Box>
        )}

        {/* The spacer mirrors the row's sticky lead block, so the dates sit
            over the right columns without either side knowing the other's
            markup — only the shared width. */}
        <Box className="flex items-end">
          <Box className={`${LEAD_WIDTH} sticky left-0 z-20 shrink-0 bg-surface`} />
          <Box className="w-24 shrink-0" />
          <DayStripHeader columns={days.columns} />
        </Box>
        {list}
      </Box>
    </Box>
  );
}

/** Daily habits say nothing — the default needs no label, and a subtext under
 *  every single row is noise rather than information. */
function scheduleLabel(schedule: Schedule, t: (k: MessageKey, p?: Record<string, number>) => string) {
  if (schedule.kind === 'daily') return undefined;
  if (schedule.kind === 'weekly') return t('schedule.weekly', { count: schedule.days.length });
  return t('schedule.timesPerWeek', { count: schedule.count });
}

/**
 * The if-then plan as one line, or nothing when there is no plan.
 *
 * Three shapes rather than one with blanks: "After , at " reads as a bug, and
 * a half-filled plan is a normal thing for a student to have written.
 */
function planLabel(habit: HabitRowView, t: ReturnType<typeof useT>): string | undefined {
  if (habit.anchor && habit.place) {
    return t('plan.sentence', { anchor: habit.anchor, place: habit.place });
  }
  if (habit.anchor) return t('plan.sentenceAnchorOnly', { anchor: habit.anchor });
  if (habit.place) return t('plan.sentencePlaceOnly', { place: habit.place });
  return undefined;
}
